import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Link } from "react-router-dom";
import { Plus, X, Upload, AlertCircle } from "lucide-react";

export default function Kunden() {
  const { user } = useAuth();
  const [kunden, setKunden] = useState([]);
  const [users, setUsers] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [showCsv, setShowCsv] = useState(false);
  const [form, setForm] = useState({
    firmenname: "",
    zustaendiger_pm: "",
    ansprechpartner: [{ name: "", email: "", telefon: "" }],
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const load = async () => {
    const [k, u] = await Promise.all([api.get("/kunden"), api.get("/users")]);
    setKunden(k.data);
    setUsers(u.data.filter((x) => x.rolle === "pm" || x.rolle === "admin"));
  };
  useEffect(() => { load(); }, []);

  const addAP = () => setForm((s) => ({ ...s, ansprechpartner: [...s.ansprechpartner, { name: "", email: "", telefon: "" }] }));
  const removeAP = (i) => setForm((s) => ({ ...s, ansprechpartner: s.ansprechpartner.filter((_, idx) => idx !== i) }));
  const updAP = (i, key, val) => setForm((s) => ({ ...s, ansprechpartner: s.ansprechpartner.map((a, idx) => (idx === i ? { ...a, [key]: val } : a)) }));

  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    try {
      const payload = {
        firmenname: form.firmenname,
        zustaendiger_pm: form.zustaendiger_pm || user.id,
        ansprechpartner: form.ansprechpartner.filter((a) => a.name.trim()),
      };
      await api.post("/kunden", payload);
      setShowForm(false);
      setForm({ firmenname: "", zustaendiger_pm: "", ansprechpartner: [{ name: "", email: "", telefon: "" }] });
      await load();
    } catch (e) {
      setErr(e.response?.data?.detail || "Fehler");
    } finally { setBusy(false); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <div className="mono-label">Verwaltung</div>
          <h1 className="font-archivo font-bold text-4xl mt-1">Kunden</h1>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setShowCsv(true)}
            data-testid="kunde-csv-btn"
            className="inline-flex items-center gap-2 px-4 py-2.5 border border-line rounded-sm text-sm hover:border-ink bg-white"
          >
            <Upload size={14} /> CSV-Import
          </button>
          <button
            onClick={() => setShowForm(true)}
            data-testid="kunde-new-btn"
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-ink text-white rounded-sm text-sm hover:bg-black"
          >
            <Plus size={14} /> Neuer Kunde
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {kunden.map((k) => (
          <Link
            key={k.id}
            to={`/kunden/${k.id}`}
            className="paper-card p-6 hover:border-ink transition-colors"
            data-testid={`kunde-card-${k.id}`}
          >
            <div className="flex items-start gap-4">
              {k.logo && (
                <div className="w-14 h-14 border border-line rounded-sm bg-white flex items-center justify-center overflow-hidden shrink-0">
                  <img src={k.logo} alt={k.firmenname} className="max-w-full max-h-full object-contain" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="mono-label">Firma</div>
                <div className="font-archivo font-bold text-xl mt-1 mb-3">{k.firmenname}</div>
                <div className="mono-label mb-1">Ansprechpartner ({(k.ansprechpartner || []).length})</div>
                <div className="space-y-1 text-sm">
                  {(k.ansprechpartner || []).slice(0, 2).map((a, i) => (
                    <div key={i} className="truncate">{a.name} · <span className="text-ink/60">{a.email}</span></div>
                  ))}
                </div>
              </div>
            </div>
          </Link>
        ))}
        {kunden.length === 0 && <div className="text-ink/50">Keine Kunden</div>}
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-6" onClick={() => setShowForm(false)}>
          <form
            onClick={(ev) => ev.stopPropagation()}
            onSubmit={submit}
            className="bg-white border border-line rounded-md p-8 w-full max-w-2xl max-h-[85vh] overflow-y-auto"
            data-testid="kunde-form"
          >
            <h2 className="font-archivo font-bold text-2xl mb-6">Neuer Kunde</h2>
            <div className="space-y-4">
              <div>
                <label className="mono-label block mb-1.5">Firmenname *</label>
                <input required data-testid="kunde-firmenname" value={form.firmenname} onChange={(e) => setForm({ ...form, firmenname: e.target.value })}
                  className="w-full px-3 py-2 border border-line rounded-sm bg-white" />
              </div>
              {user?.rolle === "admin" && (
                <div>
                  <label className="mono-label block mb-1.5">Zuständiger PM</label>
                  <select
                    data-testid="kunde-pm"
                    value={form.zustaendiger_pm}
                    onChange={(e) => setForm({ ...form, zustaendiger_pm: e.target.value })}
                    className="w-full px-3 py-2 border border-line rounded-sm bg-white"
                  >
                    <option value="">— PM wählen —</option>
                    {users.map((u) => <option key={u.id} value={u.id}>{u.name} ({u.rolle})</option>)}
                  </select>
                </div>
              )}

              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="mono-label">Ansprechpartner</span>
                  <button type="button" onClick={addAP} className="mono-label hover:opacity-100 inline-flex items-center gap-1"><Plus size={11} /> Hinzufügen</button>
                </div>
                {form.ansprechpartner.map((a, i) => (
                  <div key={i} className="grid grid-cols-1 md:grid-cols-[1fr_1fr_1fr_auto] gap-2 mb-2">
                    <input placeholder="Name" value={a.name} onChange={(e) => updAP(i, "name", e.target.value)} className="px-3 py-2 border border-line rounded-sm text-sm" />
                    <input placeholder="E-Mail" value={a.email} onChange={(e) => updAP(i, "email", e.target.value)} className="px-3 py-2 border border-line rounded-sm text-sm" />
                    <input placeholder="Telefon" value={a.telefon} onChange={(e) => updAP(i, "telefon", e.target.value)} className="px-3 py-2 border border-line rounded-sm text-sm" />
                    <button type="button" onClick={() => removeAP(i)} className="text-ink/40 hover:text-brunnerMagenta p-2"><X size={14} /></button>
                  </div>
                ))}
              </div>

              {err && <div className="text-sm text-brunnerMagenta">{err}</div>}
            </div>

            <div className="flex gap-2 justify-end mt-6 pt-4 border-t border-line">
              <button type="button" onClick={() => setShowForm(false)} className="px-4 py-2 border border-line rounded-sm">Abbrechen</button>
              <button type="submit" disabled={busy} data-testid="kunde-submit" className="px-4 py-2 bg-ink text-white rounded-sm hover:bg-black disabled:opacity-60">
                {busy ? "Anlegen…" : "Anlegen"}
              </button>
            </div>
          </form>
        </div>
      )}

      {showCsv && <CsvImportModal onClose={() => setShowCsv(false)} onDone={() => { setShowCsv(false); load(); }} />}
    </div>
  );
}

function CsvImportModal({ onClose, onDone }) {
  const [rows, setRows] = useState([]);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [ueberschreiben, setUeberschreiben] = useState(false);

  const parseCsv = (text) => {
    const lines = text.split(/\r?\n/).filter((l) => l.trim());
    if (lines.length < 2) return [];
    const headers = lines[0].split(/[;,]/).map((h) => h.trim());
    return lines.slice(1).map((line) => {
      const cols = line.split(/[;,]/);
      const obj = {}; headers.forEach((h, i) => { obj[h] = (cols[i] || "").trim(); }); return obj;
    });
  };

  const onFile = (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    const r = new FileReader();
    r.onload = async () => {
      const parsed = parseCsv(r.result);
      setRows(parsed);
      try {
        const { data } = await api.post("/kunden/csv-preview", { rows: parsed });
        setPreview(data);
      } catch {}
    };
    r.readAsText(f);
  };

  const doImport = async () => {
    setBusy(true);
    try {
      await api.post("/kunden/csv-import", { rows, ueberschreiben_duplikate: ueberschreiben });
      onDone();
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-6" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="bg-white border border-line rounded-md p-8 w-full max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="csv-modal">
        <h2 className="font-archivo font-bold text-2xl mb-2">CSV-Import Kunden</h2>
        <div className="mono-label mb-4">Spalten: <code>Firmenname, Ansprechpartner-Name, E-Mail, Telefon</code> (Trenner: „,\" oder „;\")</div>

        <input type="file" accept=".csv,text/csv" onChange={onFile} data-testid="csv-file" className="block text-sm mb-4" />

        {preview && (
          <>
            {preview.duplikate > 0 && (
              <div className="mb-3 px-3 py-2 rounded-sm bg-brunnerAmber/10 border border-brunnerAmber/30 text-brunnerAmber text-sm inline-flex items-center gap-2">
                <AlertCircle size={14} /> {preview.duplikate} Duplikat{preview.duplikate === 1 ? "" : "e"} (gleicher Firmenname)
              </div>
            )}
            <div className="border border-line rounded-sm overflow-hidden mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-paper border-b border-line">
                    <th className="text-left mono-label py-2 px-3">Firmenname</th>
                    <th className="text-left mono-label py-2 px-3">Ansprechpartner</th>
                    <th className="text-left mono-label py-2 px-3">E-Mail</th>
                    <th className="text-left mono-label py-2 px-3">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.rows.map((r, i) => (
                    <tr key={i} className="border-b border-line/60 last:border-0">
                      <td className="py-2 px-3">{r.firmenname}</td>
                      <td className="py-2 px-3">{r.ansprechpartner_name || "—"}</td>
                      <td className="py-2 px-3 text-xs font-mono">{r.email || "—"}</td>
                      <td className="py-2 px-3">
                        {r.duplikat
                          ? <span className="status-pill" style={{color:"#8A5A00", backgroundColor:"#FBEED8"}}>Duplikat</span>
                          : <span className="status-pill" style={{color:"#157A4A", backgroundColor:"#DDEEE3"}}>neu</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {preview.duplikate > 0 && (
              <label className="inline-flex items-center gap-2 text-sm mb-4">
                <input type="checkbox" checked={ueberschreiben} onChange={(e) => setUeberschreiben(e.target.checked)} data-testid="csv-ueberschreiben" />
                Duplikate ergänzen (Ansprechpartner hinzufügen)
              </label>
            )}
          </>
        )}

        <div className="flex justify-end gap-2 pt-4 border-t border-line">
          <button onClick={onClose} className="px-4 py-2 border border-line rounded-sm">Abbrechen</button>
          <button onClick={doImport} disabled={busy || !rows.length} data-testid="csv-import-btn" className="px-4 py-2 bg-ink text-white rounded-sm hover:bg-black disabled:opacity-60">
            {busy ? "Importiere…" : "Importieren"}
          </button>
        </div>
      </div>
    </div>
  );
}
